import dayjs from 'dayjs';

interface ITimeOption {
  value: string;
  label: string;
}

export const generateTimeOptions = (currentLocale = 'ar') => {
  const timeOptions: ITimeOption[] = [];
  const startTime = dayjs().startOf('day').hour(0).minute(0);
  const endTime = dayjs().startOf('day').hour(23).minute(30);
  const interval = 15;

  const locales: any = {
    ar: { am: 'ص', pm: 'م' },
    en: { am: 'AM', pm: 'PM' },
  };

  let currentTime = startTime;

  while (currentTime.isBefore(endTime) || currentTime.isSame(endTime)) {
    const hour12Format = currentTime.format('h');
    const hour24Format = currentTime.format('HH');
    const minuteFormat = currentTime.format('mm');
    const isAM = currentTime.hour() < 12;
    const label = `${hour12Format}:${minuteFormat} ${
      isAM ? locales[currentLocale].am : locales[currentLocale].pm
    }`;

    timeOptions.push({ value: `${hour24Format}:${minuteFormat}`, label });
    currentTime = currentTime.add(interval, 'minutes');
  }

  return timeOptions;
};

export const timeOptions: ITimeOption[] = generateTimeOptions();

export function convertTimeFormat(timeString: string, locale = 'ar'): string {
  const [time, meridian] = timeString.split(' ');
  const [hours, minutes] = time.split(':');

  let convertedHours = parseInt(hours, 10);

  // Check if the locale is Arabic or not
  const isArabicLocale = locale.toLowerCase() === 'ar';

  if (
    (isArabicLocale && meridian?.toLowerCase() === 'م') ||
    (!isArabicLocale &&
      meridian?.toLowerCase() === 'pm' &&
      convertedHours !== 12)
  ) {
    convertedHours += isArabicLocale ? 12 : 0;
  } else if (
    (isArabicLocale && meridian?.toLowerCase() === 'ص') ||
    (!isArabicLocale &&
      meridian?.toLowerCase() === 'am' &&
      convertedHours === 12)
  ) {
    if (convertedHours === 12) {
      convertedHours = 12;
    } else {
      convertedHours = isArabicLocale ? convertedHours % 12 : 0;
    }
  }

  const formattedHours = convertedHours?.toString().padStart(2, '0');
  const formattedMinutes = minutes?.padStart(2, '0');

  return `${formattedHours}:${formattedMinutes}`;
}

//create a function that take time in 24 hours format and return time in 12 hours format and consider locale
export function convertTimeFormatTo12(
  timeString: string,
  locale: string,
  shortcut = true
): string {
  if (!timeString || !timeString.includes(':')) {
    return timeString;
  }

  const [hoursStr, minutesStr] = timeString.split(':');
  const hours = parseInt(hoursStr, 10);
  const minutes = parseInt(minutesStr, 10);

  if (isNaN(hours) || isNaN(minutes)) {
    return timeString;
  }

  let meridian = hours >= 12 ? 'pm' : 'am';
  if (timeString.includes('pm') || timeString.includes('م')) {
    meridian = 'pm';
  } else if (timeString.includes('am') || timeString.includes('ص')) {
    meridian = 'am';
  }
  let hours12 = hours % 12;
  hours12 = hours12 ? hours12 : 12; // hour '0' should be '12'

  let meridianStr: string;

  if (locale.toLowerCase() === 'ar') {
    if (shortcut) {
      meridianStr = meridian === 'pm' ? 'م' : 'ص';
    } else {
      meridianStr = meridian === 'pm' ? 'مساءً' : 'صباحًا';
    }
  } else {
    meridianStr = meridian.toUpperCase();
  }

  const formattedMinutes = minutes.toString().padStart(2, '0');

  return `${hours12}:${formattedMinutes} ${meridianStr}`;
}

export const convertTime12to24 = (time12h: string) => {
  const [time, modifier] = time12h.split(' ');

  if (modifier) {
    const currrentTime = time.split(':');
    let hours: any = currrentTime[0];
    const minutes: string = currrentTime[1];

    if (hours === '12') {
      hours = '00';
    }
    if (modifier.toLowerCase() === 'pm' || modifier === 'م') {
      hours = parseInt(hours, 10) + 12;
    }
    // add a zero before the hours if it less than 10
    hours = hours.toString().padStart(2, '0');

    return `${hours}:${minutes}`;
  }
  return time12h;
};
